import GlobalSeoMetaTags from "components/GlobalSeoMetaTags/GlobalSeoMetaTags";
import CategorySeoText from "components/Others/CategorySeoText";
import FAQJson from "components/Others/FAQJson";
import { DEFAULT_BLUR_IMAGE_DATA_URL } from "constants/blurImage";
import { StaticContentPage } from "constants/notionDataPages";
import dayjs from "dayjs";
import Head from 'next/head';
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import Script from "next/script";
import parse from 'html-react-parser';
import { useMemo } from "react";
import LazyHydrate from "react-lazy-hydration";
import Image from "next/image";
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';


const ReactPlayer = dynamic(() => import("react-player/lazy"), { ssr: false });

const BLOG_TITLE_SUFFIX = ' | Dresden Vision';

const BLOG_DATE_FORMAT = 'dddd, D MMMM YYYY';

const MultifocalComponent = ({
  title,
  content,
  publishDate,
  seoBlock,
  seoMetaTags,
  locale,
  cover
}: StaticContentPage) => {

    const router = useRouter();

    const visibleDate = useMemo(
        () => publishDate && publishDate.start && dayjs(publishDate.start).format(BLOG_DATE_FORMAT),
        [publishDate]
      );
    
      
      const descriptionText = [ 'ca', 'nz'].includes(locale)  ? "Guide on why progressive lenses are the best for driving, reading, & computers. Buy online, or visit our optometrists at Dresden Vision." : "Guide on why multifocal lenses are the best for driving, reading, & computers. Buy online, or visit our optometrists at Dresden Vision.";
      const breadcrumbData =  [ 
          {
            id: 1,
            href: '',
            text: 'Dresden Vision'
          },
          {
            id: 2,
            href: '',
            text: title
          }
        ];
    
      let firstImgTag = false;
      //if htmlContent is null redirect to blog page
      /*useEffect(() => {
        if (!htmlContent) {
          router.push('/blog');
        } 
      }, [htmlContent, router]);*/
    
      const articleDocument = content ? parse(content, {
        replace: (domnode) => {
          const { children, name, attribs }: any = domnode;
    
          const isInternational = router.locale === 'international';
    
    
          if (!attribs?.href?.includes('https://')) {
            if (!isInternational && attribs?.href) {
              attribs.href = '/' + router.locale + attribs.href;
            }
          }
          if (name === 'img') {
            //may need to install brew install pkg-config cairo pango libpng jpeg giflib librsvg on Mac
           /*
           console.log('src: ',attribs.src);
           console.log('hash: ',attribs?.['data-hash']);
           console.log('width: ',attribs?.['data-image-width']);
           console.log('height: ',attribs?.['data-image-height']);*/
           
          
            if (firstImgTag === false) {
              firstImgTag = true;
            }
            const customClass = attribs?.['classname'] || attribs?.['className'] || attribs?.['class-name'] || '';
            return (
              <div className={`my-5 ${customClass}`}>
    
                <Image
                   src={cover.src}
                   alt={cover.alt}
                   className={cover.name}
                   objectFit="contain"
                   width={cover.width}
                   height={cover.height}
                   sizes="(min-width: 1024px) 60vw,
                 (min-width: 640px) 70vw,
                 80vw"
                   layout="responsive"
                   priority={true}
                 />
                
              </div>
            );
          }
          if (name === 'video') {
    
            const aspectRatio = parseFloat(attribs?.['data-image-width']) / parseFloat(attribs?.['data-image-height']);
    
            return (
              <div className={`h-full`}>
                <ReactPlayer
                  url={attribs.src}
                  controls={true}
                  playIcon={<div className="bg-[#00AEC7] rounded-full py-[23px] pr-[23px] pl-[28px] absolute">
                    <svg width="30" height="34" viewBox="0 0 30 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M28.3305 19.6621L4.58839 34.0909C2.92192 35.1347 0.742188 33.9604 0.742188 31.9383V3.08057C0.742188 1.05853 2.92146 -0.180968 4.58839 0.927957L28.3305 15.3568C29.8687 16.3356 29.8687 18.6834 28.3305 19.6621Z" fill="white"></path>
                    </svg></div>}
                  light={<Image
                    src={attribs?.['data-preview']}
                    alt={'video preview'}
                    placeholder="blur"
                    objectFit="contain"
                    width={350 * aspectRatio}
                    height={'350'}
                    className={'w-full'}
                    layout="fixed"
                    blurDataURL={attribs?.['data-hash']}
                  />}
                  playing={true}
                  className="my-10 max-h-96 mx-auto"
                  width={350 * aspectRatio + 'px'}
                  height={'350px'}
                />
              </div>
            );
          }
          if (name === 'figure' && attribs?.id === 'replacecenter') {
            const [imageElement] = children || [];
            const preload = !!attribs?.['data-preload'];
            return (
              <div className={'w-full'}>
                <Image
                  src={imageElement.attribs.src}
                  alt={title}
                  width={imageElement.attribs.width}
                  height={imageElement.attribs.height}
                  placeholder={preload ? undefined : 'blur'}
                  objectFit="cover"
                  layout={"fill"}
                  priority={preload}
                  blurDataURL={preload ? undefined : DEFAULT_BLUR_IMAGE_DATA_URL}
                /></div>
            );
          }
          if (name === 'script' && attribs?.id === 'replacecenter') {
            const [scriptElement] = children || [];
            const script = scriptElement.data;
            return (
              <Script
                key={'blog-script'}
                id="blog-script"
                dangerouslySetInnerHTML={{
                  __html: `
                      ${script}
                    `,
                }}
              />
            )
          }
          if (name === 'figure' && attribs?.id === 'video-poster' || attribs?.id === 'video-poster-horizontal') {
            const [imageElement] = children || [];
            const preload = !!attribs?.['data-preload'];
            return (
              <span id={attribs?.id} className={attribs?.class}>
                <Image
                  src={imageElement.attribs.src}
                  alt={title}
                  width={imageElement.attribs.width}
                  height={imageElement.attribs.height}
                  placeholder={preload ? undefined : 'blur'}
                  objectFit="cover"
                  layout="responsive"
                  priority={preload}
                  blurDataURL={preload ? undefined : DEFAULT_BLUR_IMAGE_DATA_URL}
                />
              </span>
            );
          }
          return;
        }
      }) : '';
    
      return (
        <div>
          <Head>
            <title>{title + BLOG_TITLE_SUFFIX}</title>
            <meta name="twitter:title" content={title + BLOG_TITLE_SUFFIX} />
            <meta name="og:title" content={title + BLOG_TITLE_SUFFIX} />
            <meta name="description" content={descriptionText} />
            <meta property="og:description" content={descriptionText} />
            <meta name="twitter:description" content={descriptionText} />
            <link rel="alternate" hrefLang="en" href="https://dresden.vision/multifocals"/>
            <link rel="alternate" hrefLang="en-AU" href="https://dresden.vision/au/multifocals"/>
            <link rel="alternate" hrefLang="en-NZ" href="https://dresden.vision/nz/multifocals"/>
            <link rel="alternate" hrefLang="en-CA" href="https://dresden.vision/ca/progressives"/>
          </Head>
          <div className="mx-auto mt-[32px] max-w-[1400px] px-4  text-brand-black100">
            <div className="mb-[20px]">
              <Breadcrumb data={breadcrumbData} />
            </div>
            <div className="tracking-[1px] text-brand-black100">
              <div className="mb-[24px] flex flex-col justify-between md:flex-row md:items-center">
                <h1 className="flex-auto text-[26px] font-bold uppercase ">
                  {title}
                </h1>
                <span className="flex-shrink-0 text-brand-grey300">
                  {visibleDate}
                </span>
              </div>
              <div>
                {articleDocument}</div>
                <LazyHydrate whenVisible>
              <CategorySeoText seoBlock={seoBlock} />
            </LazyHydrate>
            <FAQJson faq={seoBlock.faq} />
            </div>
          </div>
          
          <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
        </div>
      );
    };

export default MultifocalComponent;